import { CommandByteSequence, DataRuleFunction, DataRuleFunctionReturn } from '../types';
import * as Command from './commands';

const horizontalTabPosRule = (rawData: string[]): DataRuleFunctionReturn => {
  const chunkRawData: string[] = [];
  for (const byte of rawData) {
    if (chunkRawData.length === 32) {
      if (byte === Command.NUL) {
        chunkRawData.push(byte);
      }
      break;
    }

    chunkRawData.push(byte);
    if (byte === Command.NUL) break;
  }

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const selectCutModeAndCutPaperRule = (rawData: string[]): DataRuleFunctionReturn => {
  const m = rawData[0].charCodeAt(0);

  const chunkRawData: string[] = [];
  if (m >= 65 && m <= 67) chunkRawData.push(rawData[1]);

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const bitImageModeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const [m, nL, nH] = rawData.slice(0, 3).map((val) => val.charCodeAt(0));

  const chunkRawData: string[] = [];
  const dataSize = (() => {
    switch (m) {
      case 0:
      case 1:
        return nL + nH * 256;

      case 32:
      case 33:
        return (nL + nH * 256) * 3;
    }
  })();

  chunkRawData.push(...rawData.slice(3, dataSize + 3));

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const advancedRasterGraphicModeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const n = rawData[1].charCodeAt(0);

  const chunkRawData: string[] = [];
  chunkRawData.push(...rawData.slice(4, n + 4));

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const defineDownloadedBitImageModeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const [x, y] = rawData.slice(0, 2).map((val) => val.charCodeAt(0));

  const chunkRawData: string[] = [];
  const dataSize = x * y * 8;
  chunkRawData.push(...rawData.slice(4, dataSize + 4));

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const defineNVBitImageModeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const n = rawData[0].charCodeAt(0);

  const chunkRawData: string[] = [];

  let parsingPosition = 0;
  for (let index = 0; index < n; index++) {
    const [xL, xH, yL, yH] = rawData.slice(parsingPosition, 4).map((val) => val.charCodeAt(0));
    const k = (xL + Math.min(xH, 3) * 256) * (yL + Math.min(yH, 1) * 256) * 8;
    chunkRawData.push(...rawData.slice(parsingPosition, k + 4));

    parsingPosition += k + 4;
  }

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const printRasterBitImageModeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const [, xL, xH, yL, yH] = rawData.slice(0, 5).map((val) => val.charCodeAt(0));

  const chunkRawData: string[] = [];
  const dataSize = (xL + xH * 256) * (yL + yH * 256);
  chunkRawData.push(...rawData.slice(5, dataSize + 5));

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const specifyGraphicDataRule = (rawData: string[]): DataRuleFunctionReturn => {
  const [, , , fn] = rawData.map((val) => val.charCodeAt(0));

  const chunkRawData: string[] = [];
  switch (fn) {
    case 112: {
      const [xL, xH, yL, yH] = rawData.slice(8).map((val) => val.charCodeAt(0));
      const dataSize = Math.round((xL + xH * 256 + 7) / 8) * (yL + yH * 256);
      chunkRawData.push(...rawData.slice(12, dataSize + 12));
      break;
    }

    default:
      return null;
  }

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const printBarcodeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const [m, n] = rawData.slice(0, 2).map((val) => val.charCodeAt(0));

  const chunkRawData: string[] = [];
  if (m < 65) {
    for (const byte of rawData.slice(1)) {
      chunkRawData.push(byte);

      if (byte === '\x00') {
        break;
      }
    }
  }

  if (m >= 65) {
    chunkRawData.push(...rawData.slice(2, n + 2));
  }

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const print2dCodesRule = (rawData: string[]): DataRuleFunctionReturn => {
  const [pL, pH, cn, fn] = rawData.slice(0, 4).map((val) => val.charCodeAt(0));

  const chunkRawData: string[] = [];
  switch (cn) {
    case 48: {
      // PDF417 Parsing implementation
      if (fn === 80) {
        const k = pL + pH * 256 - 3;
        chunkRawData.push(...rawData.slice(5, k + 5));
      }
      break;
    }

    case 49: {
      // QR Code Parsing implementation
      if (fn === 80) {
        const k = pL + pH * 256 - 3;
        chunkRawData.push(...rawData.slice(5, k + 5));
      }
      break;
    }

    default:
      return null;
  }

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

const customUnicodeRule = (rawData: string[]): DataRuleFunctionReturn => {
  const chunkRawData: string[] = [];
  for (const byte of rawData) {
    chunkRawData.push(byte);

    if (byte === '\x00') {
      break;
    }
  }

  return {
    parsedArgs: [chunkRawData.map((val) => val.charCodeAt(0))],
    rawData: chunkRawData,
  };
};

export const getDataRule = (command: CommandByteSequence): DataRuleFunction | undefined => {
  switch (command) {
    case Command.setHorizontalTabPos:
      return horizontalTabPosRule;

    case Command.selectBitImageMode:
      return bitImageModeRule;

    case Command.printAdvanceRasterGraphic:
      return advancedRasterGraphicModeRule;

    case Command.defineDownloadedBitImage:
      return defineDownloadedBitImageModeRule;

    case Command.selectCutModeAndCutPaper:
      return selectCutModeAndCutPaperRule;

    case Command.defineNVBitImage:
      return defineNVBitImageModeRule;

    case Command.printRasterBitImage:
      return printRasterBitImageModeRule;

    case Command.specifyGraphicsData:
    case Command.specifyGraphicsDataAlternative:
      return specifyGraphicDataRule;

    case Command.printBarcode:
      return printBarcodeRule;

    case Command.print2dCodes:
      return print2dCodesRule;

    case Command.specialUnicodeEncoding:
      return customUnicodeRule;

    default:
      return undefined;
  }
};
